import axios, { AxiosInstance, AxiosResponse } from 'axios'

import { getDeploymentContract } from '../config/activeDeploymentContract'
import type { ApiResponse } from '../types'

/**
 * A base de toda chamada à API sai do CONTRATO DE DEPLOY (#996), sem fallback.
 *
 * O que havia aqui: `VITE_API_URL` com dois fallbacks. O de produção
 * (`https://nexxus-api.fly.dev`) emitia `console.warn('… usando fallback')` e SEGUIA —
 * falha aberta. Foi assim que, em 2026-09-09, um bundle servido em
 * `bbc.nexxus.dev.br` (staging) autenticou contra a API de produção sem nada quebrar.
 * Agora a variável é obrigatória e conferida no build; aqui não há o que decidir.
 *
 * A reescrita `http:// -> https://` também saiu: o contrato RECUSA API em http fora de
 * development, em vez de corrigir a configuração do operador em silêncio.
 */
const baseURL = getDeploymentContract().apiBaseUrl

const api: AxiosInstance = axios.create({
  baseURL: baseURL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
})

const refreshClient = axios.create({
  baseURL: baseURL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Function to get auth token (will be set by auth store)
let getAuthToken: () => string | null = () => null
let handleLogout: () => void = () => {}
let getActiveOrgId: () => string | null = () => null
let getRefreshToken: () => string | null = () => null
let setTokens: (accessToken: string, refreshToken?: string) => void = () => {}

// Function to set auth handlers (called by auth store)
export const setAuthHandlers = (
  getToken: () => string | null,
  logout: () => void,
  getOrgId?: () => string | null,
  getRefresh?: () => string | null,
  setAuthTokens?: (accessToken: string, refreshToken?: string) => void,
) => {
  getAuthToken = getToken
  handleLogout = logout
  if (getOrgId) getActiveOrgId = getOrgId
  if (getRefresh) getRefreshToken = getRefresh
  if (setAuthTokens) setTokens = setAuthTokens
}

// Request interceptor - add auth token, org header, and ensure HTTPS
api.interceptors.request.use(
  (config) => {
    // Add auth token if available
    const token = getAuthToken()
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }

    // Add active organization header
    const orgId = getActiveOrgId()
    if (orgId) {
      config.headers['X-Organization-Id'] = orgId
    }

    return config
  },
  (error) => {
    console.error('[API] Erro no request:', error)
    return Promise.reject(error)
  },
)

const AUTH_REFRESH_SKIP_PATHS = [
  '/api/v1/auth/login',
  '/api/v1/auth/login/json',
  '/api/v1/auth/otp/verify',
  '/api/v1/auth/otp/resend',
  '/api/v1/auth/register',
  '/api/v1/auth/refresh',
]

let refreshPromise: Promise<string | null> | null = null

const refreshAccessToken = async (): Promise<string | null> => {
  const refreshToken = getRefreshToken()
  if (!refreshToken) return null

  try {
    const response = await refreshClient.post('/api/v1/auth/refresh', {
      refresh_token: refreshToken,
    })

    const accessToken = response.data?.access_token
    const newRefreshToken = response.data?.refresh_token

    if (accessToken) {
      setTokens(accessToken, newRefreshToken ?? refreshToken)
      return accessToken
    }
    return null
  } catch (err) {
    return null
  }
}

// Response interceptor - handle errors
api.interceptors.response.use(
  (response: AxiosResponse) => {
    return response
  },
  async (error) => {
    // Handle 401 - unauthorized
    if (error.response?.status === 401) {
      const originalRequest = error.config || {}
      const requestUrl: string = originalRequest.url || ''

      const shouldSkipRefresh = AUTH_REFRESH_SKIP_PATHS.some((path) => requestUrl.includes(path))

      // Auth endpoints (login, register, etc.) — don't logout/redirect,
      // just let the error propagate so the calling page can show a toast
      if (shouldSkipRefresh) {
        return Promise.reject(error)
      }

      // Token refresh already attempted and still 401 — session expired
      if (originalRequest._retry) {
        handleLogout()
        window.location.href = '/login'
        return Promise.reject(error)
      }

      if (!refreshPromise) {
        refreshPromise = refreshAccessToken().finally(() => {
          refreshPromise = null
        })
      }

      const newToken = await refreshPromise
      if (!newToken) {
        handleLogout()
        window.location.href = '/login'
        return Promise.reject(error)
      }

      originalRequest._retry = true
      originalRequest.headers = originalRequest.headers || {}
      originalRequest.headers.Authorization = `Bearer ${newToken}`
      return api(originalRequest)
    }

    return Promise.reject(error)
  },
)

// API utility functions
export const apiClient = {
  // Generic GET request
  get: async <T>(url: string, config?: any): Promise<T> => {
    const response = await api.get<T>(url, config)
    return response.data
  },

  // Generic POST request
  post: async <T>(url: string, data?: any, config?: any): Promise<T> => {
    const response = await api.post<T>(url, data, config)
    return response.data
  },

  // Long-running POST request (e.g., sync, backup, complex operations)
  postLong: async <T>(url: string, data?: any, config?: any): Promise<T> => {
    const longConfig = {
      ...config,
      timeout: 120000, // 2 minutes for long operations
    }
    const response = await api.post<T>(url, data, longConfig)
    return response.data
  },

  // Multi-step provider workflow (e.g., a migration that does a preflight and then
  // several provider calls, each allowed its own 30 s): the client must outlive the
  // server-side state machine, or a timeout here would mean "aborted" for something
  // that the server may have carried on.
  postWorkflow: async <T>(url: string, data?: any, config?: any): Promise<T> => {
    const workflowConfig = {
      ...config,
      timeout: 300000, // 5 minutes
    }
    const response = await api.post<T>(url, data, workflowConfig)
    return response.data
  },

  // Long-running GET request (e.g., export, report generation)
  getLong: async <T>(url: string, config?: any): Promise<T> => {
    const longConfig = {
      ...config,
      timeout: 120000, // 2 minutes for long operations
    }
    const response = await api.get<T>(url, longConfig)
    return response.data
  },

  // Long-running PUT request (e.g., bulk updates, migrations)
  putLong: async <T>(url: string, data?: any, config?: any): Promise<T> => {
    const longConfig = {
      ...config,
      timeout: 120000, // 2 minutes for long operations
    }
    const response = await api.put<ApiResponse<T>>(url, data, longConfig)
    return response.data.data
  },

  // Generic PUT request
  put: async <T>(url: string, data?: any): Promise<T> => {
    const response = await api.put<T>(url, data)
    return response.data
  },

  // Generic PATCH request
  patch: async <T>(url: string, data?: any): Promise<T> => {
    const response = await api.patch<ApiResponse<T>>(url, data)
    return response.data.data
  },

  // Generic DELETE request
  delete: async <T>(url: string): Promise<T> => {
    const response = await api.delete<T>(url)
    return response.data
  },

  // Long-running DELETE (e.g., removal that cleans up a provider before answering)
  deleteLong: async <T>(url: string): Promise<T> => {
    const response = await api.delete<T>(url, { timeout: 120000 })
    return response.data
  },

  // File upload (expects ApiResponse wrapper)
  upload: async <T>(url: string, formData: FormData): Promise<T> => {
    const response = await api.post<ApiResponse<T>>(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    return response.data.data
  },

  // Form data POST (returns raw response data, for large file uploads)
  postFormData: async <T>(url: string, formData: FormData, config?: any): Promise<T> => {
    const response = await api.post<T>(url, formData, {
      ...config,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 600000, // 10 minutes for large uploads
    })
    return response.data
  },
}

// Export the API base URL for use in other components (e.g., agent installation instructions)
export const apiBaseUrl = baseURL

export default api
